import { clamp } from 'lodash';
import { MS_LIMITS } from '../enums/machine.enums';
import { getConfidenceCriteria } from '../enums/shot-rejection.enums';
import { Nullable } from '../interfaces/common/i-generics';
import { IMachineState } from '../interfaces/i-machine-state';
import {
  IAimingInputs,
  IAimingInputsWithShots,
  IAimingInputsWithTraj,
  IAimingOutputs,
  IAimingOutputsWithShots,
} from '../interfaces/i-pitch-aiming';
import { IBallState, IPlateLoc, ITrajectory } from '../interfaces/pitches';
import { IAziAltCoordinate, IPosition } from '../interfaces/pitches/i-base';
import { IMachineShot } from '../interfaces/training/i-machine-shot';
import { ITrainedMachineState } from '../interfaces/training/i-trained-machine-state';
import { BallState } from './ball-state.class';
import {
  DEG_TO_RAD,
  FTPS_TO_MPH,
  RAD_TO_DEG,
  meanObject,
} from './math.utilities';
import { ShotRejectionHelper } from './shot-rejection.helper';
import { TrajHelper } from './trajectory.helper';

export class AimingHelper {
  private static getSafeRelease = (config: IAimingInputs) => {
    const output: IPosition = {
      px: config.bs.px,
      pz: config.bs.pz,
      // use releaseFt instead of bs.py
      py: config.releaseFt,
    };

    return output;
  };

  // forces the position of bs, traj, and ms to match the given release position
  private static translateToRelease(config: IAimingInputs): {
    bs: IBallState;
    traj: ITrajectory;
    ms: IMachineState;
  } {
    const newRelease = this.getSafeRelease(config);

    const translatedTraj: ITrajectory = {
      ...config.traj,
      ...newRelease,
    };

    return {
      bs: {
        ...config.bs,
        ...newRelease,
      },
      ms: {
        ...config.ms,
        ...newRelease,
      },
      traj: translatedTraj,
    };
  }

  // using the mean traj from the shots provided
  static aimWithShots(
    config: IAimingInputsWithShots
  ): IAimingOutputsWithShots | undefined {
    try {
      // filter shots by confidence
      const confCriteria = getConfidenceCriteria(config.priority);
      const safeShots =
        config.training && config.checkConfidence
          ? // while training and confidence checks are enabled, filter shots by confidence
            config.aimingShots.filter((s) => {
              if (s.bs_origin === 'manual') {
                // any shot created w/ manual detection is assumed to be usable
                return true;
              }

              const { reject } = ShotRejectionHelper.filterShot({
                shot: s,
                criteria: confCriteria,
              });

              return !reject;
            })
          : // for BP or when confidence checks are disabled, use all shots (from TMS)
            config.aimingShots;

      const aimed = this.aimWithShotsHelper({
        ...config,
        aimingShots: safeShots,
      });

      if (config.clampTilt) {
        aimed.ms.tilt = clamp(
          aimed.ms.tilt,
          MS_LIMITS.TILT.MIN,
          MS_LIMITS.TILT.MAX
        );
      }

      return {
        ...aimed,
        usingShots: safeShots,
      };
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  // using the traj provided
  static aimWithTraj(
    config: IAimingInputsWithTraj
  ): IAimingOutputs | undefined {
    try {
      return this.aimChars({
        ...config,
        traj: config.aimingTraj,
      });
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  /** a mean traj will be computed using shots, which then be used as the traj for the aim function (instead of the traj provided in chars) */
  private static aimWithShotsHelper(
    config: IAimingInputs & { aimingShots: IMachineShot[] }
  ): IAimingOutputs {
    // ? sanity check, but maybe we don't need to filter at all
    const safeShots = config.aimingShots.filter((s) => !s.hitter_present);

    const aimingTraj = this.getAimingTraj({
      refMs: config.ms,
      shots: safeShots,
      releaseFt: config.releaseFt,
    });

    if (!aimingTraj) {
      // there are no usable shots
      return this.aimChars(config);
    }

    return this.aimChars({
      ...config,
      traj: aimingTraj,
    });
  }

  // todo: make sure this is in foundations repo
  /** aims the given chars at the given plate */
  private static aimChars(config: IAimingInputs): IAimingOutputs {
    const safeRelease = this.getSafeRelease(config);

    const rotation = TrajHelper.getAltAzForTranslation(
      config.traj,
      safeRelease,
      config.targetPlate
    );

    const translated = this.translateToRelease(config);

    const rotatedTraj = TrajHelper.rotateTraj(translated.traj, rotation);
    const rotatedBs = new BallState(translated.bs).rotateBallState(rotation);

    const rotatedMs = {
      ...translated.ms,
      tilt:
        translated.ms.tilt +
        rotation.altitude_rad * (config.stepSize ?? 1) * RAD_TO_DEG,
      yaw:
        translated.ms.yaw +
        rotation.azimuth_rad * (config.stepSize ?? 1) * RAD_TO_DEG,
    };

    const output: IAimingOutputs = {
      bs: rotatedBs,
      traj: rotatedTraj,
      ms: rotatedMs,
      usingTraj: rotatedTraj,
      aimingTraj: translated.traj,
    };

    return output;
  }

  /** returns average traj of matching shots (after rotation to match pitch),
   * or the original pitch traj if insufficient matching shots are found
   */
  static getAimingTraj = (config: {
    refMs: IMachineState;
    shots: IMachineShot[];
    releaseFt: number;
  }): ITrajectory | undefined => {
    if (config.shots.length === 0) {
      return undefined;
    }

    const rotatedTrajs = config.shots.map((shot) => {
      const baseTraj = shot.user_traj ?? shot.traj;

      // translate to desired release position
      const translatedTraj = TrajHelper.translateTraj(baseTraj, {
        px: config.refMs.px,
        pz: config.refMs.pz,
        py: config.releaseFt,
      });

      // rotate to match ms
      const rotation: IAziAltCoordinate = {
        altitude_rad: (config.refMs.tilt - shot.actual_ms.tilt) * DEG_TO_RAD,
        azimuth_rad: (config.refMs.yaw - shot.actual_ms.yaw) * DEG_TO_RAD,
      };

      /** rotate each shot */
      return TrajHelper.rotateTraj(translatedTraj, rotation);
    });

    return meanObject(rotatedTrajs);
  };

  // used in spins priority pitch building
  static aimBsTraj(config: {
    releasePos: IPosition;
    bs: IBallState;
    traj: ITrajectory;
    plate: IPlateLoc;
  }): {
    bs: IBallState;
    traj: ITrajectory;
    plate: IPlateLoc;
  } {
    const { traj, bs, plate, releasePos } = config;

    const safeTraj: ITrajectory = {
      ...traj,
      px: releasePos.px,
      py: releasePos.py,
      pz: releasePos.pz,
    };

    const aimedTraj = TrajHelper.aimTrajectory(safeTraj, releasePos, plate);

    const aimedBs = {
      ...bs,
      px: releasePos.px,
      py: releasePos.py,
      pz: releasePos.pz,
      vx: aimedTraj.vx * FTPS_TO_MPH,
      vy: aimedTraj.vy * FTPS_TO_MPH,
      vz: aimedTraj.vz * FTPS_TO_MPH,
    };

    return {
      bs: aimedBs,
      traj: aimedTraj,
      plate: plate,
    };
  }

  // encapsulated for CI testing
  static getAimingParams(
    tms: ITrainedMachineState,
    useContinuousTraining: boolean // whether we should try to use CT-related fields (if possible)
  ): { aimingTraj: Nullable<ITrajectory>; ms: IMachineState } {
    if (useContinuousTraining) {
      // ? return CT params (where possible) if CT is enabled, fallback to non-CT params
      return {
        aimingTraj: tms.contAimingTraj ?? tms.aimingTraj,
        ms: tms.contMs ?? tms.ms,
      };
    }

    // ? always return non-CT params if CT is disabled
    return {
      aimingTraj: tms.aimingTraj,
      ms: tms.ms,
    };
  }
}
